

import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Grid,
  Chip,
  TextField,
  InputAdornment,
  Typography,
  CircularProgress,
  Box,
  Pagination,
  Stack,
  Button,
  Menu,
  MenuItem,
  Fade,
  ToggleButtonGroup,
} from "@mui/material";
 
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

 
// Icons
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { useAuth } from 'auth/Auth';
import { useTravelUi } from 'hooks/useTravelUi';
import { useTheme } from '@mui/material/styles';
import CallButton from 'components/Softphone/CallButton';
 
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';

import Inventory2Icon from '@mui/icons-material/Inventory2';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import ContactsIcon from '@mui/icons-material/Contacts';
import CircleIcon from '@mui/icons-material/Circle';


// Add new icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import StageIcon from '@mui/icons-material/Flag';
import EventIcon from '@mui/icons-material/Event';
import BadgeIcon from '@mui/icons-material/Badge';
import WorkIcon from '@mui/icons-material/Work';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import InventoryIcon from '@mui/icons-material/Inventory';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FactoryIcon from '@mui/icons-material/Factory';
import CategoryIcon from '@mui/icons-material/Category';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import LuggageIcon from '@mui/icons-material/Luggage';
import PaymentIcon from '@mui/icons-material/Payment';
import GroupsIcon from '@mui/icons-material/Groups';
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';



const ITEMS_PER_PAGE = 20;

const typeColors = {
  leads: {
    light: '#E3F2FD',
    main: '#2196F3',
    dark: '#1976D2'
  },
  users: {
    light: '#E8F5E9',
    main: '#4CAF50',
    dark: '#388E3C'
  },
  deals: {
    light: '#FFF3E0',
    main: '#FF9800',
    dark: '#F57C00'
  },
  products: {
    light: '#F3E5F5',
    main: '#9C27B0',
    dark: '#7B1FA2'
  },
  accounts: {
    light: '#E8EAF6',
    main: '#3F51B5',
    dark: '#303F9F'
  },
  contacts: {
    light: '#FFEBEE',
    main: '#F44336',
    dark: '#D32F2F'
  },
  trips: {
    light: '#E0F7FA',
    main: '#00ACC1',
    dark: '#00838F'
  },
  trip_packages: {
    light: '#E8F5E9',
    main: '#43A047',
    dark: '#2E7D32'
  },
  travel_orders: {
    light: '#FFF8E1',
    main: '#FFB300',
    dark: '#FF8F00'
  },
  package_components: {
    light: '#FBE9E7',
    main: '#FF7043',
    dark: '#E64A19'
  },
  departure_dates: {
    light: '#EDE7F6',
    main: '#7E57C2',
    dark: '#5E35B1'
  },
  payment_templates: {
    light: '#E3F2FD',
    main: '#1E88E5',
    dark: '#1565C0'
  },
  travel_order_pax: {
    light: '#FCE4EC',
    main: '#EC407A',
    dark: '#C2185B'
  },
  // Add a default color scheme for any unexpected types
  default: {
    light: '#FAFAFA',
    main: '#9E9E9E',
    dark: '#616161'
  }
};


// First, add this function to calculate counts for each type
const getTypeCounts = (results) => {
  const counts = results.reduce((acc, result) => {
    acc[result.type] = (acc[result.type] || 0) + 1;
    return acc;
  }, {});
  
  // Add total count for 'all'
  counts.all = results.length;
  
  return counts;
};


// Define the filter types configuration
const baseFilterTypes = [
  { id: 'all', label: 'All', Icon: SearchIcon },
  { id: 'leads', label: 'Leads', Icon: BusinessIcon },
  { id: 'users', label: 'Users', Icon: PersonIcon },
  { id: 'deals', label: 'Deals', Icon: HandshakeIcon },
  { id: 'products', label: 'Products', Icon: Inventory2Icon, legacyCrm: true },
  { id: 'accounts', label: 'Accounts', Icon: AccountBalanceIcon },
  { id: 'contacts', label: 'Contacts', Icon: ContactsIcon },
];

const travelFilterTypes = [
  { id: 'trips', label: 'Destinations', Icon: FlightTakeoffIcon },
  { id: 'trip_packages', label: 'Packages', Icon: Inventory2Icon },
  { id: 'travel_orders', label: 'Travel Orders', Icon: LuggageIcon },
  { id: 'package_components', label: 'Logistics', Icon: MiscellaneousServicesIcon },
  { id: 'departure_dates', label: 'Departures', Icon: EventIcon },
  { id: 'payment_templates', label: 'Payment Templates', Icon: PaymentIcon },
  { id: 'travel_order_pax', label: 'Passengers', Icon: GroupsIcon },
];

const LEGACY_CRM_SEARCH_TYPES = new Set(['products', 'orders', 'quotations']);
 
function SearchResults() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState(searchParams.get('query') || '');
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [selectedType, setSelectedType] = useState('all');
  const [activeFilters, setActiveFilters] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
 
  const searchRef = useRef(null);
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();
  const { showTravel } = useTravelUi();
  // v2 keeps Products / Quotations / Orders for travel tenants (Sales app);
  // classic still hides them when Travel is on.
  const isV2 = useTheme()?.pearlUi === 'v2';
  const hideLegacyCommerce = showTravel && !isV2;
 

  useEffect(() => {
    const trimmedQuery = searchParams.get('query')?.trim() || '';
    if (trimmedQuery.length >= 2) {
      setSearchQuery(trimmedQuery);
      fetchSearchResults();
    }
  }, [searchParams, showTravel, hideLegacyCommerce]);


  const fetchSearchResults = async () => {
    setIsLoading(true);
    try {
      const trimmedQuery = searchQuery.trim();
      // Use URLSearchParams to properly encode the query
      const params = new URLSearchParams({
        query: trimmedQuery
      });
      
      const response = await axiosInstance.get(`/search?${params.toString()}`);
      const rows = Array.isArray(response.data) ? response.data : [];
      setSearchResults(
        rows.filter((result) => {
          const type = result?.type;
          if (hideLegacyCommerce && LEGACY_CRM_SEARCH_TYPES.has(type)) return false;
          if (!showTravel && travelFilterTypes.some((f) => f.id === type)) return false;
          return true;
        }),
      );
      setPage(1);
    } catch (error) {
      console.error('Error fetching search results:', error);
      if (error.response?.status === 404) {
        setSearchResults([]);
      }
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setPage(1); // Reset to first page when changing type
  };
 
 
 
  // Add the missing functions here
  const handleResultClick = (result) => {
    switch (result.type) {
      case 'leads':
        navigate(`/leads/${result.id}`);
        break;
      case 'users':
        navigate(`/users/${result.id}/profile`);
        break;
      case 'deals':
        navigate(`/deals/${result.id}`);
        break;
      case 'products':
        navigate(`/products/${result.id}`);
        break;
      case 'accounts':
        navigate(`/accounts/${result.id}`);
        break;
      case 'contacts':
        navigate(`/contacts/${result.id}`);
        break;
      case 'orders':
        navigate(`/orders/${result.id}`);
        break;
      case 'invoices':
        navigate(`/invoices`);
        break;
      case 'quotation':
      case 'quotations':
        navigate(`/quote/${result.id}`);
        break;
      case 'case':
      case 'cases':
        navigate(`/cases/${result.id}`);
        break;
      case 'trips':
        navigate(`/trips/detail/${result.id}`);
        break;
      case 'trip_packages':
        navigate(`/trip-packages/${result.id}`);
        break;
      case 'travel_orders':
        navigate(`/travel-order/${result.id}`);
        break;
      case 'package_components':
        navigate(`/trip-package-components/${result.id}`);
        break;
      case 'departure_dates':
        navigate(`/trip-departure-dates/${result.id}`);
        break;
      case 'payment_templates':
        navigate(`/payment-templates/${result.id}`);
        break;
      case 'travel_order_pax':
        navigate(`/travel-order-pax/${result.id}`);
        break;
      default:
        break;
    }
  };
 
  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchQuery(value);
    
    // Only clear results if search is empty
    if (!value.trim()) {
      setSearchResults([]);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmedQuery = searchQuery.trim();
    if (trimmedQuery.length >= 2) {
      const newSearchParams = new URLSearchParams();
      // Properly encode the search query while preserving spaces
      newSearchParams.set('query', trimmedQuery);
      setSearchParams(newSearchParams);
      fetchSearchResults();
    }
  };

  const processResults = (results) => {
    let processed = [...results];
 
    // Apply type filter
    if (selectedType !== 'all') {
      processed = processed.filter(item => item.type === selectedType);
    }
 
    // Apply sorting
    switch (sortBy) {
      case 'newest':
        processed.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        break;
      case 'alphabetical':
        processed.sort((a, b) => getName(a).localeCompare(getName(b)));
        break;
      case 'type':
        processed.sort((a, b) => a.type.localeCompare(b.type));
        break;
    }
 
    return processed;
  };
 
  const processedResults = processResults(searchResults);
  console.log(processedResults);
 
  const totalPages = Math.ceil(processedResults.length / ITEMS_PER_PAGE);
  const paginatedResults = processedResults.slice(
    (page - 1) * ITEMS_PER_PAGE,
    page * ITEMS_PER_PAGE
  );
 
  const getName = (result) => {
    switch (result.type) {
      case 'leads': {
        const firstName = result.first_name || '';
        const lastName = result.last_name || '';
        return [firstName, lastName].filter(Boolean).join(' ');
      }
      case 'users':
        return result.username || '';
      case 'deals':
        return result.name || '';
      case 'products':
        return result.name || '';
      case 'accounts':
        return result.name || '';
      case 'contacts': {
        const firstName = result.first_name || '';
        const lastName = result.last_name || '';
        return [firstName, lastName].filter(Boolean).join(' ');
      }
      case 'orders':
        return result.order_number || '';
      case 'invoices':
        return result.invoice_number || '';
      case 'quotation':
      case 'quotations':
        return result.quote_name || '';
      case 'case':
      case 'cases':
        return result.case_number || '';
      case 'trips':
        return result.primary_country || result.title || result.name || '';
      case 'trip_packages':
        return result.title || result.name || '';
      case 'travel_orders':
        return result.name || '';
      case 'package_components':
        return result.name || '';
      case 'departure_dates':
        return result.name || result.trip_country || result.departure_city || '';
      case 'payment_templates':
        return result.template_name || result.name || '';
      case 'travel_order_pax':
        return (
          result.passenger_name ||
          [result.first_name, result.last_name].filter(Boolean).join(' ') ||
          result.name ||
          ''
        );
      default:
        return result.name || result.title || '';
    }
  };
 
  const handleViewModeChange = (_, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
    }
  };

  const getIcon = (type, size = 40) => {
    // Get the color scheme, fallback to default if type doesn't exist
    const colorScheme = typeColors[type] || typeColors.default;
    
    const iconProps = {
      sx: {
        fontSize: size,
        color: colorScheme.main
      }
    };
  
    switch (type) {
      case 'leads':
        return <BusinessIcon {...iconProps} />;
      case 'users':
        return <PersonIcon {...iconProps} />;
      case 'deals':
        return <HandshakeIcon {...iconProps} />;
      case 'products':
        return <Inventory2Icon {...iconProps} />;
      case 'accounts':
        return <AccountBalanceIcon {...iconProps} />;
      case 'contacts':
        return <ContactsIcon {...iconProps} />;
      case 'orders':
        return <ShoppingCartIcon {...iconProps} />;
      case 'invoices':
        return <AttachMoneyIcon {...iconProps} />;
      case 'quotation':
      case 'quotations':
        return <LocalOfferIcon {...iconProps} />;
      case 'case':
      case 'cases':
        return <SupportAgentIcon {...iconProps} />;
      case 'trips':
        return <FlightTakeoffIcon {...iconProps} />;
      case 'trip_packages':
        return <Inventory2Icon {...iconProps} />;
      case 'travel_orders':
        return <LuggageIcon {...iconProps} />;
      case 'package_components':
        return <MiscellaneousServicesIcon {...iconProps} />;
      case 'departure_dates':
        return <EventIcon {...iconProps} />;
      case 'payment_templates':
        return <PaymentIcon {...iconProps} />;
      case 'travel_order_pax':
        return <GroupsIcon {...iconProps} />;
      default:
        return <CircleIcon {...iconProps} />; // Default icon for unknown types
    }
  };


  const getResultContent = (result) => {
    const iconStyle = { fontSize: 16, mr: 1, verticalAlign: 'text-bottom' };
    
    // Helper function to safely display text and check for null/undefined
    const safeText = (value) => value || '';
  
    // Helper function to render a field only if it has a value
    const renderField = (value, Icon, prefix = '') => {
      if (!value) return null;
      return (
        <Typography variant="body2" color="text.secondary">
          <Icon sx={iconStyle} />{prefix}{safeText(value)}
        </Typography>
      );
    };

    /**
     * A phone number with a call button next to it.
     *
     * Searching for someone and then having to open their record just to find
     * the dial button is the long way round to a phone call. CallButton
     * renders nothing when telephony is off, so this stays identical to
     * renderField on tenants without the module.
     */
    const renderPhone = (value, context, prefix = '') => {
      if (!value) return null;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            <PhoneIcon sx={iconStyle} />{prefix}{safeText(value)}
          </Typography>
          <CallButton phone={String(value)} variant="icon" size="small" {...context} />
        </Box>
      );
    };
  
    switch (result.type) {
      case 'users':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">
              {safeText(`${result.first_name || ''} ${result.last_name || ''}`.trim())}
            </Typography>
            {renderField(result.email, EmailIcon)}
            {renderField(result.username, PersonIcon)}
            {renderField(result.role, AdminPanelSettingsIcon)}
            {result.is_active !== undefined && (
              <Chip
                size="small"
                label={result.is_active ? 'Active' : 'Inactive'}
                color={result.is_active ? 'success' : 'default'}
                sx={{ width: 'fit-content' }}
              />
            )}
          </Stack>
        );
  
      case 'leads':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">
              {safeText(`${result.first_name || ''} ${result.last_name || ''}`.trim())}
            </Typography>
            {renderField(result.company_name, BusinessIcon)}
            {renderField(result.email, EmailIcon)}
            {renderPhone(result.phone, { parentTable: 'leads', parentId: result.id, leadId: result.id })}
            {renderPhone(result.mobile, { parentTable: 'leads', parentId: result.id, leadId: result.id }, 'Mobile: ')}
            {renderField(result.lead_status, CheckCircleIcon, 'Status: ')}
          </Stack>
        );
  
      case 'deals':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.name)}</Typography>
            {renderField(result.account_name, BusinessIcon, 'Account: ')}
            {result.value && (
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon sx={iconStyle} />₹{Number(result.value).toLocaleString()}
              </Typography>
            )}
            {renderField(result.stage, StageIcon, 'Stage: ')}
            {result.close_date && renderField(
              new Date(result.close_date).toLocaleDateString(),
              EventIcon,
              'Close Date: '
            )}
          </Stack>
        );
  
      case 'products':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.name)}</Typography>
            {renderField(result.product_code, LocalOfferIcon, 'Code: ')}
            {renderField(result.manufacturer, FactoryIcon)}
            {result.price && (
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon sx={iconStyle} />₹{Number(result.price).toLocaleString()}
              </Typography>
            )}
            {renderField(result.category, CategoryIcon)}
            {renderField(result.sku, InventoryIcon, 'SKU: ')}
          </Stack>
        );
  
      case 'accounts':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.name)}</Typography>
            {renderField(result.email, EmailIcon)}
            {renderPhone(result.phone, { parentTable: 'accounts', parentId: result.id, accountId: result.id })}
            {renderPhone(result.mobile, { parentTable: 'accounts', parentId: result.id, accountId: result.id }, 'Mobile: ')}
            {renderField(result.account_type, BusinessIcon, 'Type: ')}
            {renderField(result.account_status, CheckCircleIcon, 'Status: ')}
            {renderField(result.manager_name, PersonIcon, 'Manager: ')}
          </Stack>
        );
  
      case 'contacts':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">
              {safeText(`${result.first_name || ''} ${result.last_name || ''}`.trim())}
            </Typography>
            {renderField(result.email, EmailIcon)}
            {renderPhone(result.phone, { parentTable: 'contacts', parentId: result.id, contactId: result.id, accountId: result.account_id })}
            {renderPhone(result.mobile_number, { parentTable: 'contacts', parentId: result.id, contactId: result.id, accountId: result.account_id }, 'Mobile: ')}
            {renderField(result.title, BadgeIcon)}
            {renderField(result.department, WorkIcon)}
            {result.account_name && (
              <Typography variant="body2" color="text.secondary">
                <BusinessIcon sx={iconStyle} />Company: {result.account_name}
              </Typography>
            )}
          </Stack>
        );
  
      case 'orders':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.order_number)}</Typography>
            {renderField(result.contact_name, PersonIcon, 'Contact: ')}
            {result.total_amount && (
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon sx={iconStyle} />
                Total: ₹{Number(result.total_amount).toLocaleString()}
              </Typography>
            )}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
            {renderField(result.account_name, BusinessIcon, 'Account: ')}
          </Stack>
        );
  
      case 'invoices':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.invoice_number)}</Typography>
            {renderField(result.client_name, PersonIcon, 'Client: ')}
            {result.total_amount && (
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon sx={iconStyle} />
                Total: ₹{Number(result.total_amount).toLocaleString()}
              </Typography>
            )}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
            {result.due_date && renderField(
              new Date(result.due_date).toLocaleDateString(),
              EventIcon,
              'Due Date: '
            )}
          </Stack>
        );
  
      case 'quotation':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.quote_name)}</Typography>
            {renderField(result.account_name, BusinessIcon, 'Account: ')}
            {result.total_amount && (
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon sx={iconStyle} />
                Total: ₹{Number(result.total_amount).toLocaleString()}
              </Typography>
            )}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
            {result.quote_date && renderField(
              new Date(result.quote_date).toLocaleDateString(),
              EventIcon,
              'Date: '
            )}
          </Stack>
        );
  
      case 'case':
      case 'cases':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.case_number)}</Typography>
            {renderField(result.subject, BusinessIcon, 'Subject: ')}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
            {renderField(result.account_name, BusinessIcon, 'Account: ')}
            {renderField(result.contact_name, PersonIcon, 'Contact: ')}
            {renderField(result.case_source, LocalOfferIcon, 'Source: ')}
          </Stack>
        );

      case 'trips':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">
              {safeText(result.primary_country || result.name || result.title)}
            </Typography>
            {renderField(result.continent, LocalOfferIcon, 'Continent: ')}
            {renderField(result.currency, AttachMoneyIcon, 'Currency: ')}
            {renderField(result.category, CategoryIcon, 'Category: ')}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
          </Stack>
        );

      case 'trip_packages':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.title || result.name)}</Typography>
            {renderField(
              result.trip_country || result.trip_title,
              FlightTakeoffIcon,
              'Destination: '
            )}
            {renderField(result.city, BusinessIcon, 'City: ')}
            {renderField(result.package_tier, LocalOfferIcon, 'Tier: ')}
            {result.base_price != null && (
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon sx={iconStyle} />
                {`${result.currency || ''} ${Number(result.base_price).toLocaleString()}`.trim()}
              </Typography>
            )}
            {result.duration_days != null && renderField(`${result.duration_days} days`, EventIcon, 'Duration: ')}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
          </Stack>
        );

      case 'travel_orders':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.name)}</Typography>
            {renderField(result.primary_contact_email, EmailIcon)}
            {renderField(result.primary_contact_phone, PhoneIcon)}
            {result.order_total != null && (
              <Typography variant="body2" color="text.secondary">
                <AttachMoneyIcon sx={iconStyle} />
                Total: {Number(result.order_total).toLocaleString()}
              </Typography>
            )}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
            {renderField(result.payment_status, PaymentIcon, 'Payment: ')}
            {renderField(result.channel, LocalOfferIcon, 'Channel: ')}
          </Stack>
        );

      case 'package_components':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.name)}</Typography>
            {renderField(result.category, CategoryIcon, 'Category: ')}
            {renderField(result.sub_category, LocalOfferIcon, 'Sub Category: ')}
            {renderField(result.property_name, BusinessIcon, 'Property: ')}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
          </Stack>
        );

      case 'departure_dates':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">
              {safeText(result.trip_country || result.trip_title || result.name)}
            </Typography>
            {result.departure_date && renderField(
              new Date(result.departure_date).toLocaleDateString(),
              EventIcon,
              'Departure: '
            )}
            {renderField(result.departure_city, BusinessIcon, 'City: ')}
            {renderField(result.package_title, Inventory2Icon, 'Package: ')}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
          </Stack>
        );

      case 'payment_templates':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">{safeText(result.template_name || result.name)}</Typography>
            {renderField(result.payment_type, PaymentIcon, 'Type: ')}
            {renderField(result.status, CheckCircleIcon, 'Status: ')}
          </Stack>
        );

      case 'travel_order_pax':
        return (
          <Stack spacing={1}>
            <Typography variant="h6">
              {safeText(
                result.passenger_name ||
                  `${result.first_name || ''} ${result.last_name || ''}`.trim() ||
                  result.name
              )}
            </Typography>
            {renderField(result.email, EmailIcon)}
            {renderField(result.mobile, PhoneIcon)}
            {renderField(result.travel_order_name, LuggageIcon, 'Order: ')}
          </Stack>
        );
  
      default:
        return (
          <Typography variant="body1">{safeText(result.name || result.title)}</Typography>
        );
    }
  };


  const typeCounts = getTypeCounts(searchResults);
  const filterTypes = [
    ...baseFilterTypes.filter((filter) => !(hideLegacyCommerce && filter.legacyCrm)),
    ...(showTravel
      ? travelFilterTypes.filter((filter) => (typeCounts[filter.id] || 0) > 0)
      : []),
  ];

  // For a more polished look, you can also create a custom component for the pill:
  const FilterPill = ({ id, label, Icon, count = 0, isSelected, onClick }) => (
    <Chip
      icon={<Icon />}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {label}
          <Box
            component="span"
            sx={{
              bgcolor: isSelected ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.08)',
              px: 1,
              py: 0.5,
              borderRadius: '12px',
              fontSize: '0.75rem',
              lineHeight: 1,
              fontWeight: 'medium',
              minWidth: '24px',
              textAlign: 'center'
            }}
          >
            {count}
          </Box>
        </Box>
      }
      onClick={onClick}
      color={isSelected ? 'primary' : 'default'}
      variant={isSelected ? 'filled' : 'outlined'}
      sx={{
        px: 2,
        height: 'auto',
        '&:hover': { transform: 'translateY(-2px)' },
        transition: 'transform 0.2s ease',
        '& .MuiChip-label': {
          px: 1,
          py: 0.75
        }
      }}
    />
  );



  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox
            mx={2}
            mt={-3}
            py={3}
            px={2}
            variant="gradient"
            bgColor="info"
            borderRadius="lg"
            coloredShadow="info"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              background: 'linear-gradient(45deg, #1565C8 30%, #5B9BD5 90%)',
              boxShadow: '0 8px 16px rgba(0, 158, 219, 0.3)'
            }}
          >
            <MDTypography variant="h6" color="white">
              Search Results
            </MDTypography>
            <Box>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={handleViewModeChange}
                size="small"
                sx={{
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  '& .MuiToggleButton-root': {
                    color: 'white',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      color: 'white',
                    },
                  },
                }}
              >
              </ToggleButtonGroup>
            </Box>
          </MDBox>
 
          <MDBox pt={3} px={2}>
            <Box sx={{
              display: 'flex',
              gap: 2,
              mb: 4,
              flexDirection: { xs: 'column', sm: 'row' }
            }}>

              <Box sx={{ flexGrow: 1 }}>
                <form onSubmit={handleSearch}>  {/* Add form wrapper */}
                  <TextField
                    fullWidth
                    value={searchQuery}
                    onChange={handleSearchChange}
                    placeholder="Search for leads, users, or deals..."
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: 'info.main' }} />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          {isLoading && <CircularProgress size={20} color="info" />}
                        </InputAdornment>
                      ),
                      sx: {
                        borderRadius: '12px',
                        '& fieldset': {
                          borderWidth: '2px',
                          borderColor: 'rgba(0,0,0,0.1)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'info.main',
                        },
                      }
                    }}
                    sx={{
                      '& .MuiInputBase-root': {
                        height: '48px',
                        fontWeight: 500,
                      }
                    }}
                  />
                </form>
              </Box>

            </Box>


            {/* Type Filter Pills with Counts */}
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              mb: 4, 
              flexWrap: 'wrap',
              justifyContent: 'center'
            }}>
              {filterTypes.map(({ id, label, Icon }) => (
                <FilterPill
                  key={id}
                  id={id}
                  label={label}
                  Icon={Icon}
                  count={typeCounts[id] || 0}
                  isSelected={selectedType === id}
                  onClick={() => handleTypeSelect(id)}
                />
              ))}
            </Box>


            {/* Sort Menu */}
            <Menu
              anchorEl={sortAnchorEl}
              open={Boolean(sortAnchorEl)}
              onClose={() => setSortAnchorEl(null)}
              TransitionComponent={Fade}
            >
              <MenuItem
                onClick={() => {
                  setSortBy('newest');
                  setSortAnchorEl(null);
                }}
                selected={sortBy === 'newest'}
              >
                Newest First
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSortBy('alphabetical');
                  setSortAnchorEl(null);
                }}
                selected={sortBy === 'alphabetical'}
              >
                Alphabetical
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setSortBy('type');
                  setSortAnchorEl(null);
                }}
                selected={sortBy === 'type'}
              >
                By Type
              </MenuItem>
            </Menu>
 
            {/* Active Filters Display */}
            {activeFilters.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {activeFilters.map(filter => (
                  <Chip
                    key={filter}
                    label={filter.charAt(0).toUpperCase() + filter.slice(1)}
                    onDelete={() => {
                      setActiveFilters(prev => prev.filter(f => f !== filter));
                    }}
                    color="primary"
                    variant="outlined"
                    size="small"
                  />
                ))}
                <Chip
                  label="Clear All"
                  onClick={() => setActiveFilters([])}
                  size="small"
                  color="error"
                />
              </Box>
            )}
 
            {/* Results Display */}
            <MDBox mt={2}>
              {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : paginatedResults.length > 0 ? (
                <>
                  {viewMode === 'grid' ? (
                    <Grid container spacing={3}>
                      {paginatedResults.map((result) => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={`${result.type}-${result.id}`}>
                          <Card
                            onClick={() => handleResultClick(result)}
                            sx={{
                              height: '100%',
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                              '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.15)'
                              },
                              borderRadius: '12px',
                              overflow: 'hidden'
                            }}
                          >
                            <Box sx={{ p: 2 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                justifyContent: 'space-between', 
                                alignItems: 'center',
                                mb: 2 
                              }}>
                                {getIcon(result.type)}
                                <Chip
                                  label={result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                                  size="small"
                                  sx={{
                                    backgroundColor: typeColors[result.type]?.light || typeColors.default.light,
                                    color: typeColors[result.type]?.dark || typeColors.default.dark
                                  }}
                                />
                              </Box>
                              {getResultContent(result)}
                            </Box>
                          </Card>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Stack spacing={2}>
                      {paginatedResults.map((result) => (
                        <Card
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleResultClick(result)}
                          sx={{
                            transition: 'all 0.3s ease',
                            cursor: 'pointer',
                            '&:hover': {
                              transform: 'translateX(4px)',
                              boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                            },
                            borderRadius: '12px'
                          }}
                        >
                          <Box sx={{ p: 2 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              justifyContent: 'space-between', 
                              alignItems: 'flex-start'
                            }}>
                              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flex: 1 }}>
                                {getIcon(result.type)}
                                {getResultContent(result)}
                              </Box>
                              <Chip
                                label={result.type.charAt(0).toUpperCase() + result.type.slice(1)}
                                size="small"
                                sx={{
                                  backgroundColor: typeColors[result.type]?.light || typeColors.default.light,
                                  color: typeColors[result.type]?.dark || typeColors.default.dark,
                                  ml: 2
                                }}
                              />
                            </Box>
                          </Box>
                        </Card>
                      ))}
                    </Stack>
                  )}
                  
                  {/* Enhanced Pagination */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 4, mb: 2 }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Showing {((page - 1) * ITEMS_PER_PAGE) + 1} to{' '}
                      {Math.min(page * ITEMS_PER_PAGE, processedResults.length)} of{' '}
                      {processedResults.length} results
                    </Typography>
                    
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(_, value) => {
                        setPage(value);
                        searchRef.current?.scrollIntoView({ behavior: 'smooth' });
                      }}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            transform: 'scale(1.1)',
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              ) : searchQuery.length >= 2 && !isLoading ? (
                <Box
                  sx={{
                    textAlign: 'center',
                    py: 8,
                    px: 3,
                    backgroundColor: 'grey.50',
                    borderRadius: 2
                  }}
                >
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No results found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your search or filters to find what you're looking for
                  </Typography>
                  {activeFilters.length > 0 && (
                    <Button
                      variant="outlined"
                      onClick={() => setActiveFilters([])}
                      sx={{ mt: 2 }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </Box>
              ) : null}
            </MDBox>
          </MDBox>
        </Card>
      </MDBox>
      
    </DashboardLayout>
  );
}
 
export default SearchResults;